import { baseApi, getApiOrganizationId } from '@/shared/api';
import { isDevelopment } from '../utils';
import { Monitoring } from './monitoring';

export const USAGE_EVENTS = {
  view: ['open'],
  saved_presets: [
    'create_preset',
    'update_preset',
    'edit_preset',
    'delete_preset',
    'share_preset',
    'unshare_preset',
    'set_as_default',
    'unset_as_default',
    'favorite_preset',
    'unfavorite_preset',
  ],
  shared_preset: ['create', 'apply'],
  data_table: [
    'simple_sorting',
    'advanced_sorting',
    'reorder_column',
    'resize_column',
  ],
  tree_data_grid: ['reorder_column', 'resize_column'],
  data_grid_autosize: [
    'columns_expand',
    'columns_shrink',
    'columns_default_width',
  ],
  grid_type_switch: ['data_table', 'tree_data_grid'],
  date_range_filter: ['change', 'select_custom_option'],
  string_filter: ['type_change', 'sorting'],
  media_filter: ['type_change'],
  filters_and_settings_panel: ['change_value'],
  highlight: ['change_levels_count'],
  metric_coloring: ['change_levels_count', 'delete_coloring'],
  time_machine: ['undo', 'redo', 'reset'],
  refresh_view_data: ['refresh'],
  download_view_data: ['download'],
} as const satisfies Record<string, readonly string[]>;

type UsageEventMap = {
  [F in keyof typeof USAGE_EVENTS]: (typeof USAGE_EVENTS)[F][number];
};

type UsageFeature = keyof UsageEventMap;

export type UsageEventProps = Record<string, string | number | boolean>;

interface UsageEventPayload {
  id: string;
  page: string;
  feature: string;
  action: string;
  props: UsageEventProps;
  occurredAt: string;
}

interface QueuedUsageEvent extends UsageEventPayload {
  organizationId: string;
}

const FLUSH_SIZE = 20;
const FLUSH_INTERVAL_MS = 10000;
const MAX_QUEUE = 100;
const MAX_BATCH = 50;
const RETRY_BACKOFF_MS = 30000;

let queue: QueuedUsageEvent[] = [];
let currentPage = '';
let failureReported = false;
let flushing = false;
let flushPending = false;
let retryAfter = 0;

export const setUsageEventPage = (page: string) => {
  currentPage = page;
};

const enqueue = (feature: string, action: string, props: UsageEventProps) => {
  const organizationId = getApiOrganizationId();

  if (isDevelopment() || !currentPage || !organizationId) {
    return;
  }

  queue.push({
    id: crypto.randomUUID(),
    organizationId,
    page: currentPage,
    feature,
    action,
    props,
    occurredAt: new Date().toISOString(),
  });

  if (queue.length >= FLUSH_SIZE && Date.now() >= retryAfter) {
    void flushUsageEvents();
  }
};

export const trackEvent = <F extends UsageFeature>(
  feature: F,
  action: UsageEventMap[F],
  props: UsageEventProps = {},
) => enqueue(feature, action, props);

const isDeclaredEvent = (feature: string, action: string) =>
  Object.hasOwn(USAGE_EVENTS, feature) &&
  (USAGE_EVENTS as Record<string, readonly string[]>)[feature].includes(action);

export const trackDeclaredEvent = (
  feature: string,
  action: string,
  props: UsageEventProps = {},
) => {
  if (!isDeclaredEvent(feature, action)) {
    return;
  }

  enqueue(feature, action, props);
};

const reportFlushFailure = (error: Error) => {
  if (failureReported) {
    return;
  }

  failureReported = true;
  Monitoring.captureError(error, { tags: { usageEvents: 'flush' } });
};

const RETRYABLE_REJECTIONS: readonly number[] = [401, 429];

const isRejectedBatch = (status: number | null) =>
  typeof status === 'number' &&
  status >= 400 &&
  status < 500 &&
  !RETRYABLE_REJECTIONS.includes(status);

const toPayload = (event: QueuedUsageEvent): UsageEventPayload => ({
  id: event.id,
  page: event.page,
  feature: event.feature,
  action: event.action,
  props: event.props,
  occurredAt: event.occurredAt,
});

export const flushUsageEvents = async () => {
  if (flushing) {
    flushPending = true;

    return;
  }

  const organizationId = getApiOrganizationId();
  queue = organizationId
    ? queue.filter((event) => event.organizationId === organizationId)
    : [];

  if (queue.length === 0) {
    return;
  }

  flushing = true;
  const batch = queue.slice(0, MAX_BATCH);
  queue = queue.slice(MAX_BATCH);

  try {
    const { status, error } = await baseApi.functions.invokeKeepalive(
      'track-usage-events',
      { events: batch.map(toPayload) },
    );

    if (!error) {
      retryAfter = 0;

      return;
    }

    if (isRejectedBatch(status)) {
      reportFlushFailure(error);

      return;
    }

    queue = [...batch, ...queue].slice(-MAX_QUEUE);
    retryAfter = Date.now() + RETRY_BACKOFF_MS;
    reportFlushFailure(error);
  } finally {
    flushing = false;

    if (flushPending) {
      flushPending = false;

      if (queue.length > 0) {
        void flushUsageEvents();
      }
    }
  }
};

export const startUsageEventFlushing = () => {
  const interval = setInterval(() => {
    void flushUsageEvents();
  }, FLUSH_INTERVAL_MS);

  const onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      void flushUsageEvents();
    }
  };

  document.addEventListener('visibilitychange', onVisibilityChange);

  return () => {
    clearInterval(interval);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    void flushUsageEvents();
  };
};
